import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import axios from 'axios';
import MixpanelAnalytics, { ANALYTICS_EVENTS } from '../analytics/mixpanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      MixpanelAnalytics.identify(response.data.id, {
        email: response.data.email,
        firstName: response.data.full_name?.split(' ')[0],
      });
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [fetchCurrentUser]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      await fetchCurrentUser();
      MixpanelAnalytics.track(ANALYTICS_EVENTS.USER_SIGNED_IN, { login_method: 'email_password' });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  }, [fetchCurrentUser]);

  const register = useCallback(async (email, password, fullName) => {
    try {
      await axios.post(`${API}/auth/register`, { email, password, full_name: fullName });
      MixpanelAnalytics.track(ANALYTICS_EVENTS.USER_SIGNED_UP, { registration_method: 'email_password' });
      return await login(email, password);
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  }, [login]);

  const logout = useCallback(() => {
    MixpanelAnalytics.track(ANALYTICS_EVENTS.USER_SIGNED_OUT);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    MixpanelAnalytics.reset();
  }, []);

  const contextValue = useMemo(() => ({
    user, login, register, logout, loading, fetchCurrentUser, API
  }), [user, login, register, logout, loading, fetchCurrentUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export { API };
export default AuthContext;
